if (!customElements.get('fox-newsletter-popup')) {
  class FoxNewsletterPopup extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('form');
      this.result = this.querySelector('[data-popup-result]');
      if (!this.dialog || !this.form || typeof this.dialog.showModal !== 'function') return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.sectionId = this.dataset.sectionId;
      this.designMode = this.dataset.designMode === 'true';
      this.seenKey = `foxmag-newsletter-popup:${this.sectionId}:seen`;
      this.pendingKey = 'foxmag-newsletter-popup:pending';
      this.subscribedKey = 'foxmag-newsletter-popup:subscribed';
      this.querySelectorAll('[data-popup-close]').forEach(button => {
        button.addEventListener('click', () => this.dialog.close(), { signal });
      });
      this.dialog.addEventListener('close', () => this.onClose(), { signal });
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      }, { signal });
      this.form.addEventListener('submit', () => {
        if (this.designMode) return;
        this.writeStorage('sessionStorage', this.pendingKey, JSON.stringify({ sectionId: this.sectionId, time: Date.now() }));
        const submit = this.form.querySelector('[type="submit"]');
        submit.disabled = true;
        this.form.setAttribute('aria-busy', 'true');
      }, { signal });
      if (this.designMode) {
        if (this.dataset.previewSuccess === 'true') this.dataset.state = 'success';
        this.form.addEventListener('submit', event => event.preventDefault(), { signal });
        document.addEventListener('shopify:section:select', event => {
          if (event.detail?.sectionId === this.sectionId) this.open(true);
        }, { signal });
        document.addEventListener('shopify:section:deselect', event => {
          if (event.detail?.sectionId === this.sectionId) this.dialog.close();
        }, { signal });
        return;
      }
      window.addEventListener('pageshow', () => {
        this.form.querySelector('[type="submit"]').disabled = false;
        this.form.removeAttribute('aria-busy');
      }, { signal });
      const pending = this.readPending();
      const sourceMatches = new URLSearchParams(location.search).get('newsletter_popup') === this.sectionId || pending?.sectionId === this.sectionId;
      if (sourceMatches && (this.result.dataset.success === 'true' || this.result.dataset.errors === 'true')) {
        this.removeStorage('sessionStorage', this.pendingKey);
        if (this.result.dataset.success === 'true') {
          this.markSubscribed();
          this.clearReturnUrl();
        }
        this.open(true);
        return;
      }
      if (this.result.dataset.success === 'true') {
        this.markSubscribed();
        return;
      }
      if (this.dataset.enabled !== 'true' || this.isSuppressed()) return;
      this.timer = window.setTimeout(() => this.tryOpen(), Math.max(0, Number(this.dataset.delay) || 0) * 1000);
    }

    readStorage(storage, key) {
      try { return window[storage].getItem(key); } catch { return null; }
    }

    writeStorage(storage, key, value) {
      try { window[storage].setItem(key, value); } catch { /* Storage may be disabled. */ }
    }

    removeStorage(storage, key) {
      try { window[storage].removeItem(key); } catch { /* Storage may be disabled. */ }
    }

    readPending() {
      try {
        const pending = JSON.parse(this.readStorage('sessionStorage', this.pendingKey));
        return pending && Date.now() - pending.time < 15 * 60 * 1000 ? pending : null;
      } catch { return null; }
    }

    isSuppressed() {
      if (this.dataset.customerSubscribed === 'true') return true;
      if (this.readStorage('localStorage', this.subscribedKey) || this.readStorage('sessionStorage', this.subscribedKey)) return true;
      const seenAt = Number(this.readStorage('localStorage', this.seenKey) || this.readStorage('sessionStorage', this.seenKey));
      return seenAt > 0 && Date.now() - seenAt < Math.max(1, Number(this.dataset.cooldown) || 7) * 86400000;
    }

    rememberSeen() {
      if (this.designMode) return;
      const timestamp = String(Date.now());
      this.writeStorage('localStorage', this.seenKey, timestamp);
      this.writeStorage('sessionStorage', this.seenKey, timestamp);
    }

    markSubscribed() {
      this.dataset.state = 'success';
      this.writeStorage('localStorage', this.subscribedKey, 'true');
      this.writeStorage('sessionStorage', this.subscribedKey, 'true');
    }

    clearReturnUrl() {
      // A refresh must not replay the thank-you dialog after it was dismissed.
      const url = new URL(location.href);
      if (url.searchParams.get('newsletter_popup') === this.sectionId) url.searchParams.delete('newsletter_popup');
      url.searchParams.delete('customer_posted');
      if (url.hash === `#${this.form.id}`) url.hash = '';
      try { window.history.replaceState(window.history.state, '', url); } catch { /* The confirmation still works without History access. */ }
    }

    startSuccessTimer() {
      window.clearTimeout(this.successTimer);
      const note = this.querySelector('[data-popup-auto-close-note]');
      const seconds = Math.min(60, Math.max(0, Number(this.dataset.successAutoClose) || 0));
      if (note) note.hidden = true;
      if (this.designMode || this.dataset.state !== 'success' || !seconds) return;
      if (note) {
        note.textContent = `Închidere automată după ${seconds} s. Poți închide și din X.`;
        note.hidden = false;
      }
      this.successTimer = window.setTimeout(() => {
        if (this.dialog.open) this.dialog.close();
      }, seconds * 1000);
    }

    tryOpen() {
      if (!this.isConnected || this.isSuppressed()) return;
      const isMobile = window.matchMedia('(max-width: 767px)').matches;
      if ((isMobile && this.dataset.showMobile !== 'true') || (!isMobile && this.dataset.showDesktop !== 'true')) return;
      if (document.visibilityState === 'hidden' || document.querySelector('dialog[open], .popup-modal[open], cart-drawer.active, header-drawer > details.drawer[open]')) {
        this.timer = window.setTimeout(() => this.tryOpen(), 500);
        return;
      }
      this.open();
    }

    open(force = false) {
      if (!this.isConnected || this.dialog.open || (!force && this.dataset.enabled !== 'true')) return;
      if (this.dataset.state === 'success') {
        this.dialog.setAttribute('aria-labelledby', `NewsletterPopupSuccessHeading-${this.sectionId}`);
        this.dialog.setAttribute('aria-describedby', `NewsletterPopupSuccessDescription-${this.sectionId}`);
      }
      this.dialog.showModal();
      document.documentElement.classList.add('fox-newsletter-popup-open');
      this.startSuccessTimer();
      if (!force) this.rememberSeen();
    }

    onClose() {
      window.clearTimeout(this.successTimer);
      document.documentElement.classList.remove('fox-newsletter-popup-open');
      this.rememberSeen();
    }

    disconnectedCallback() {
      window.clearTimeout(this.timer);
      window.clearTimeout(this.successTimer);
      this.controller?.abort();
      if (this.dialog?.open) {
        this.dialog.close();
        document.documentElement.classList.remove('fox-newsletter-popup-open');
      }
      this.controller = null;
    }
  }
  customElements.define('fox-newsletter-popup', FoxNewsletterPopup);
}
